<template>
  <!-- ── LEFT: Live Preview panel (lg+ only) ── -->
  <div class="hidden lg:flex flex-col flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-[#0d1117]" style="width:880px">

    <!-- Preview header -->
    <div class="flex-shrink-0 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-[#1C2128]">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span class="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
          <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Live preview</p>
        </div>
        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
          :class="typeColor(questionType)">
          <span v-html="typeIconSm(questionType)" class="scale-75"></span>
          {{ typeLabel(questionType) }}
        </span>
      </div>
      <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-1">Exactly how your bookers will see this question</p>
    </div>

    <!-- Preview scrollable body -->
    <div class="flex-1 overflow-y-auto flex flex-col items-center justify-start bg-gray-100 dark:bg-[#0d1117] px-3 pt-3 pb-5">

      <!-- Browser chrome -->
      <div class="w-full rounded-t-xl px-3 py-2 flex items-center gap-2" style="background:#e2e3e5">
        <div class="flex gap-1.5">
          <div class="w-3 h-3 rounded-full" style="background:#ff5f57"></div>
          <div class="w-3 h-3 rounded-full" style="background:#febc2e"></div>
          <div class="w-3 h-3 rounded-full" style="background:#28c840"></div>
        </div>
        <div class="flex-1 rounded-md px-2 py-1 flex items-center justify-center gap-1 text-[11px] text-gray-500 font-medium truncate" style="background:white">
          <svg class="w-3 h-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path fill-rule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clip-rule="evenodd"/>
          </svg>
          <span class="truncate">timelydo.com/{{ globalStore.user.url || 'your-name' }}/event-name</span>
        </div>
      </div>

      <!-- Booking page card: 960×700 inner, scaled to 880×642 at 0.9167 -->
      <div class="w-full rounded-b-xl overflow-hidden relative" style="height:642px">
        <div style="position:absolute;top:0;left:0;width:960px;height:700px;transform:scale(0.9167);transform-origin:top left;display:flex;background:white;overflow:hidden">

          <!-- ══ LEFT PANEL — 40% (384px) ══ -->
          <div style="width:384px;flex-shrink:0;border-right:1px solid #e5e7eb;display:flex;flex-direction:column;overflow:hidden">

            <!-- TOP: banner + avatar + name + divider -->
            <div style="flex-shrink:0">
              <!-- Banner -->
              <div style="height:128px;background:linear-gradient(to right,#3b82f6,#6366f1,#3b82f6)"></div>

              <!-- Avatar -->
              <div style="display:flex;justify-content:center;margin-top:-48px;position:relative;z-index:10">
                <div style="padding:2px;background:white;border-radius:9999px">
                  <div style="width:96px;height:96px;border-radius:9999px;background:#d1d5db"></div>
                </div>
              </div>

              <!-- Name + designation -->
              <div style="display:flex;flex-direction:column;align-items:center;gap:8px;margin-top:8px;padding:0 20px">
                <div style="height:20px;width:144px;background:#e5e7eb;border-radius:4px"></div>
                <div style="height:14px;width:96px;background:#f3f4f6;border-radius:4px"></div>
              </div>

              <!-- Social icons -->
              <div style="display:flex;justify-content:center;gap:8px;margin-top:8px;padding:0 20px">
                <div v-for="i in 3" :key="i" style="width:24px;height:24px;border-radius:9999px;background:#f3f4f6"></div>
              </div>

              <!-- Divider -->
              <div style="margin-top:12px;border-top:1px solid #f3f4f6"></div>
            </div>

            <!-- MIDDLE: event details + about -->
            <div style="flex:1;padding:16px 20px;overflow:hidden">

              <!-- EVENT label -->
              <div style="height:12px;width:40px;background:#e5e7eb;border-radius:4px;margin-bottom:12px"></div>

              <!-- Event name card -->
              <div style="border-radius:5px;background:#f3f4f6;padding:12px 16px;margin-bottom:16px">
                <div style="height:16px;width:75%;background:#e5e7eb;border-radius:4px"></div>
              </div>

              <!-- Detail rows: icon + text -->
              <div style="display:flex;flex-direction:column;gap:12px">
                <div v-for="(w, i) in [64, 112, 160]" :key="i" style="display:flex;align-items:center;gap:10px">
                  <div style="width:28px;height:28px;border-radius:9999px;background:#eff6ff;flex-shrink:0"></div>
                  <div style="height:14px;background:#f3f4f6;border-radius:4px" :style="'width:' + w + 'px'"></div>
                </div>
              </div>

              <!-- About label + lines -->
              <div style="margin-top:20px">
                <div style="height:12px;width:40px;background:#e5e7eb;border-radius:4px;margin-bottom:8px"></div>
                <div v-for="(w, i) in ['100%','83%','75%']" :key="i"
                  style="height:12px;background:#f3f4f6;border-radius:4px;margin-bottom:6px"
                  :style="'width:' + w"></div>
              </div>
            </div>

            <!-- BOTTOM: branding -->
            <div style="flex-shrink:0;border-top:1px solid #f3f4f6;padding:12px 20px">
              <div style="height:24px;width:96px;background:#f3f4f6;border-radius:4px"></div>
            </div>
          </div>

          <!-- ══ RIGHT PANEL — 60% (576px) ══ -->
          <div style="flex:1;display:flex;flex-direction:column;overflow:hidden">

            <!-- Header: back button + "ENTER DETAILS" -->
            <div style="display:flex;align-items:center;gap:10px;padding:20px 20px 16px;border-bottom:1px solid #f3f4f6;flex-shrink:0">
              <div style="width:28px;height:28px;border-radius:9999px;background:#f3f4f6;display:flex;align-items:center;justify-content:center;flex-shrink:0">
                <svg width="13" height="13" fill="none" stroke="#9ca3af" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
                </svg>
              </div>
              <span style="font-size:11px;font-weight:700;color:#9ca3af;letter-spacing:0.08em;text-transform:uppercase">Enter details</span>
            </div>

            <!-- Form body -->
            <div style="flex:1;padding:20px 20px 16px;overflow:hidden">

              <!-- Name skeleton -->
              <div style="margin-bottom:16px">
                <div style="height:10px;width:40px;background:#e5e7eb;border-radius:3px;margin-bottom:6px"></div>
                <div style="height:36px;width:536px;background:#f3f4f6;border-radius:8px"></div>
              </div>

              <!-- Email skeleton -->
              <div style="margin-bottom:16px">
                <div style="height:10px;width:40px;background:#e5e7eb;border-radius:3px;margin-bottom:6px"></div>
                <div style="height:36px;width:536px;background:#f3f4f6;border-radius:8px"></div>
              </div>

              <!-- ── Question — settings/profile/general design pattern ── -->
              <div style="margin-bottom:16px">

                <!-- Label: uppercase small, same as settings page -->
                <div style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:6px">
                  {{ title.trim() ? (title.length > 48 ? title.slice(0, 48) + '…' : title) : 'Your question here...' }}
                </div>

                <!-- one_line / phone_number / email / url -->
                <template v-if="['one_line','phone_number','email','url'].includes(questionType)">
                  <input v-model="previewAnswer"
                    :type="questionType === 'email' ? 'email' : questionType === 'url' ? 'url' : questionType === 'phone_number' ? 'tel' : 'text'"
                    :placeholder="previewPlaceholder"
                    :maxlength="questionType === 'one_line' && previewMaxLength ? previewMaxLength : undefined"
                    style="display:block;width:536px;max-width:536px;padding:10px 14px;font-size:13px;color:#374151;background:white;border:1px solid #e5e7eb;border-radius:8px;outline:none;box-sizing:border-box;font-family:inherit" />
                  <div v-if="questionType === 'one_line' && previewMaxLength !== null"
                    style="display:flex;justify-content:flex-end;width:536px;margin-top:4px">
                    <span :style="{ fontSize:'11px', color: previewCharsRemaining <= 0 ? '#ef4444' : '#9ca3af' }">
                      {{ previewCharsRemaining }} {{ previewCharsRemaining === 1 ? 'char' : 'chars' }} remaining
                    </span>
                  </div>
                </template>

                <!-- multi_line -->
                <template v-else-if="questionType === 'multi_line'">
                  <textarea v-model="previewAnswer" rows="3" :placeholder="previewPlaceholder"
                    :maxlength="previewMaxLength || undefined"
                    style="display:block;width:536px;max-width:536px;padding:10px 14px;font-size:13px;color:#374151;background:white;border:1px solid #e5e7eb;border-radius:8px;outline:none;resize:none;box-sizing:border-box;font-family:inherit;line-height:1.5"></textarea>
                  <div v-if="previewMaxLength !== null"
                    style="display:flex;justify-content:flex-end;width:536px;margin-top:4px">
                    <span :style="{ fontSize:'11px', color: previewCharsRemaining <= 0 ? '#ef4444' : '#9ca3af' }">
                      {{ previewCharsRemaining }} {{ previewCharsRemaining === 1 ? 'char' : 'chars' }} remaining
                    </span>
                  </div>
                </template>

                <!-- number -->
                <template v-else-if="questionType === 'number'">
                  <input v-model="previewAnswer" type="number" :placeholder="previewPlaceholder"
                    :min="questionDetails.min || undefined" :max="questionDetails.max || undefined"
                    style="display:block;width:536px;max-width:536px;padding:10px 14px;font-size:13px;color:#374151;background:white;border:1px solid #e5e7eb;border-radius:8px;outline:none;box-sizing:border-box;font-family:inherit" />
                </template>

                <!-- boolean -->
                <template v-else-if="questionType === 'boolean'">
                  <div style="display:flex;gap:16px;padding-top:4px">
                    <label style="display:flex;align-items:center;gap:6px;cursor:pointer">
                      <input type="radio" name="prev_bool" value="yes" v-model="previewAnswer" style="accent-color:#3b82f6;width:14px;height:14px" />
                      <span style="font-size:13px;color:#374151">Yes</span>
                    </label>
                    <label style="display:flex;align-items:center;gap:6px;cursor:pointer">
                      <input type="radio" name="prev_bool" value="no" v-model="previewAnswer" style="accent-color:#3b82f6;width:14px;height:14px" />
                      <span style="font-size:13px;color:#374151">No</span>
                    </label>
                  </div>
                </template>

                <!-- date (with sub-modes) -->
                <template v-else-if="questionType === 'date'">
                  <!-- date (default) -->
                  <input v-if="!questionDetails.date_mode || questionDetails.date_mode === 'date'"
                    v-model="previewAnswer" type="date"
                    :min="questionDetails.min_date || undefined" :max="questionDetails.max_date || undefined"
                    style="display:block;width:536px;max-width:536px;padding:10px 14px;font-size:13px;color:#374151;background:white;border:1px solid #e5e7eb;border-radius:8px;outline:none;box-sizing:border-box;font-family:inherit" />
                  <!-- date_range: single range calendar via v-date-picker -->
                  <v-date-picker v-else-if="questionDetails.date_mode === 'date_range'"
                    v-model="previewDateRange"
                    mode="date"
                    is-range
                    :is-dark="isDark"
                    :min-date="questionDetails.min_date || undefined"
                    :max-date="questionDetails.max_date || undefined"
                    :popover="{ visibility: 'click' }">
                    <template #default="{ inputValue, inputEvents }">
                      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;width:536px">
                        <div style="position:relative">
                          <svg style="position:absolute;left:12px;top:50%;transform:translateY(-50%);width:14px;height:14px;stroke:#9ca3af;fill:none;stroke-width:1.75;pointer-events:none" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                          </svg>
                          <input :value="inputValue.start" v-on="inputEvents.start"
                            placeholder="Start date" readonly
                            style="display:block;width:100%;padding:10px 14px 10px 36px;font-size:13px;color:#374151;background:white;border:1px solid #e5e7eb;border-radius:8px;outline:none;box-sizing:border-box;font-family:inherit;cursor:pointer" />
                        </div>
                        <div style="position:relative">
                          <svg style="position:absolute;left:12px;top:50%;transform:translateY(-50%);width:14px;height:14px;stroke:#9ca3af;fill:none;stroke-width:1.75;pointer-events:none" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                          </svg>
                          <input :value="inputValue.end" v-on="inputEvents.end"
                            placeholder="End date" readonly
                            style="display:block;width:100%;padding:10px 14px 10px 36px;font-size:13px;color:#374151;background:white;border:1px solid #e5e7eb;border-radius:8px;outline:none;box-sizing:border-box;font-family:inherit;cursor:pointer" />
                        </div>
                      </div>
                    </template>
                  </v-date-picker>
                  <!-- time: dropdown with 30-minute slots -->
                  <select v-else-if="questionDetails.date_mode === 'time'"
                    v-model="previewAnswer"
                    style="display:block;width:536px;max-width:536px;padding:10px 14px;font-size:13px;color:#374151;background:white;border:1px solid #e5e7eb;border-radius:8px;outline:none;cursor:pointer;box-sizing:border-box;font-family:inherit">
                    <option value="" disabled>Select a time</option>
                    <option v-for="opt in filteredPreviewTimeOptions()" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  </select>
                  <!-- datetime -->
                  <input v-else-if="questionDetails.date_mode === 'datetime'"
                    v-model="previewAnswer" type="datetime-local"
                    style="display:block;width:536px;max-width:536px;padding:10px 14px;font-size:13px;color:#374151;background:white;border:1px solid #e5e7eb;border-radius:8px;outline:none;box-sizing:border-box;font-family:inherit" />
                </template>

                <!-- drop_down -->
                <template v-else-if="questionType === 'drop_down'">
                  <select v-if="previewOptions.length" v-model="previewAnswer"
                    style="display:block;width:536px;max-width:536px;padding:10px 14px;font-size:13px;color:#374151;background:white;border:1px solid #e5e7eb;border-radius:8px;outline:none;cursor:pointer;box-sizing:border-box;font-family:inherit">
                    <option value="" disabled selected>Select an option</option>
                    <option v-for="opt in previewOptions" :key="opt" :value="opt">{{ opt }}</option>
                  </select>
                  <div v-else style="padding:10px 14px;font-size:12px;color:#d1d5db;font-style:italic;border:1px solid #e5e7eb;border-radius:8px">Add options to see the dropdown</div>
                </template>

                <!-- radio_button -->
                <template v-else-if="questionType === 'radio_button'">
                  <div v-if="previewOptions.length" style="display:flex;flex-direction:column;gap:8px;padding-top:4px">
                    <label v-for="opt in previewOptions.slice(0, 4)" :key="opt" style="display:flex;align-items:center;gap:8px;cursor:pointer">
                      <input type="radio" name="prev_radio" :value="opt" v-model="previewAnswer" style="accent-color:#3b82f6;width:14px;height:14px;flex-shrink:0" />
                      <span style="font-size:13px;color:#374151">{{ opt }}</span>
                    </label>
                  </div>
                  <div v-else style="padding:10px 14px;font-size:12px;color:#d1d5db;font-style:italic;border:1px solid #e5e7eb;border-radius:8px">Add options to see the radio buttons</div>
                </template>

                <!-- check_box -->
                <template v-else-if="questionType === 'check_box'">
                  <div v-if="previewOptions.length" style="display:flex;flex-direction:column;gap:8px;padding-top:4px">
                    <label v-for="opt in previewOptions.slice(0, 4)" :key="opt" style="display:flex;align-items:center;gap:8px;cursor:pointer">
                      <input type="checkbox" :value="opt"
                        :checked="previewChecked.includes(opt)"
                        @change="e => e.target.checked ? previewChecked.push(opt) : previewChecked.splice(previewChecked.indexOf(opt), 1)"
                        style="accent-color:#3b82f6;width:14px;height:14px;border-radius:3px;flex-shrink:0" />
                      <span style="font-size:13px;color:#374151">{{ opt }}</span>
                    </label>
                  </div>
                  <div v-else style="padding:10px 14px;font-size:12px;color:#d1d5db;font-style:italic;border:1px solid #e5e7eb;border-radius:8px">Add options to see the checkboxes</div>
                </template>

                <!-- rating -->
                <template v-else-if="questionType === 'rating'">
                  <div v-if="(questionDetails.scale || '5').toString() === '5'" style="display:flex;gap:6px;padding-top:4px">
                    <button v-for="i in 5" :key="i" type="button"
                      @click="previewRating = previewRating === i ? null : i">
                      <svg style="width:22px;height:22px" fill="currentColor" viewBox="0 0 576 512"
                        :style="previewRating !== null && previewRating >= i ? 'color:#fbbf24' : 'color:#e5e7eb'">
                        <path d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"/>
                      </svg>
                    </button>
                  </div>
                  <div v-else style="display:flex;flex-wrap:wrap;gap:5px;padding-top:4px">
                    <button v-for="i in 11" :key="i" type="button"
                      @click="previewRating = previewRating === i - 1 ? null : i - 1"
                      style="width:26px;height:26px;border-radius:5px;font-size:11px;font-weight:600;border:1px solid;cursor:pointer"
                      :style="previewRating !== null && previewRating === i - 1
                        ? 'background:#2563eb;border-color:#2563eb;color:white'
                        : 'background:white;border-color:#e5e7eb;color:#6b7280'">
                      {{ i - 1 }}
                    </button>
                  </div>
                </template>

                <!-- file_upload -->
                <template v-else-if="questionType === 'file_upload'">
                  <div style="border:1px dashed #d1d5db;border-radius:8px;padding:12px 14px;display:flex;align-items:center;gap:8px;background:#fafafa">
                    <svg width="14" height="14" fill="none" stroke="#9ca3af" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
                    </svg>
                    <span style="font-size:13px;color:#9ca3af">
                      Choose file{{ questionDetails.max_size_mb ? ' · max ' + questionDetails.max_size_mb + ' MB' : '' }}
                    </span>
                  </div>
                  <p v-if="questionDetails.allowed_types?.length" style="font-size:11px;color:#9ca3af;margin-top:4px">
                    Accepted: {{ questionDetails.allowed_types.map(t => '.' + t).join(', ') }}
                  </p>
                </template>

                <!-- hidden -->
                <template v-else-if="questionType === 'hidden'">
                  <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:10px 14px">
                    <span style="font-size:13px;color:#64748b">🙈 Hidden from bookers</span>
                  </div>
                </template>

                <!-- signature -->
                <template v-else-if="questionType === 'signature'">
                  <p v-if="questionDetails.declaration_text?.trim()"
                    style="font-size:12px;color:#6b7280;margin-bottom:8px;line-height:1.5">
                    {{ questionDetails.declaration_text }}
                  </p>
                  <div style="border:1px solid #e5e7eb;border-radius:8px;overflow:hidden">
                    <div style="width:536px;height:100px;background:white;display:flex;align-items:center;justify-content:center">
                      <span style="font-size:12px;color:#d1d5db;font-style:italic;user-select:none">Sign here</span>
                    </div>
                    <div style="display:flex;align-items:center;justify-content:space-between;padding:6px 12px;border-top:1px solid #f3f4f6;background:#f9fafb">
                      <span style="font-size:10px;color:#9ca3af">Draw your signature in the box above</span>
                      <span style="font-size:10px;font-weight:600;color:#d1d5db">Clear</span>
                    </div>
                  </div>
                </template>

              </div>
              <!-- end question field -->

              <!-- Submit button -->
              <div style="width:536px;background:#2563eb;border-radius:5px;padding:12px;text-align:center">
                <span style="font-size:13px;font-weight:500;color:white">Schedule Event</span>
              </div>

            </div>
          </div>
          <!-- end right panel -->

        </div>
      </div>
      <!-- end scaled page -->

    </div><!-- end preview scrollable body -->
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { useQuestionTypes } from 'composables/useQuestionTypes.js'
import { useGlobalStore } from 'stores/global.js'

const props = defineProps({
  questionType:    { type: String, required: true },
  title:           { type: String, default: '' },
  questionDetails: { type: Object, default: () => ({}) },
})

const { typeLabel, typeColor, typeIconSm } = useQuestionTypes()
const globalStore = useGlobalStore()

const previewAnswer    = ref('')
const previewChecked   = reactive([])
const previewRating    = ref(null)
const previewDateRange = reactive({ start: null, end: null })

const isDark = document.documentElement.classList.contains('dark')

const TIME_OPTIONS = (() => {
  const opts = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hh = String(h).padStart(2, '0')
      const mm = String(m).padStart(2, '0')
      const val = `${hh}:${mm}`
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
      const period = h < 12 ? 'AM' : 'PM'
      opts.push({ value: val, label: `${h12}:${mm} ${period}` })
    }
  }
  return opts
})()

function filteredPreviewTimeOptions() {
  const min = props.questionDetails?.min_time || ''
  const max = props.questionDetails?.max_time || ''
  return TIME_OPTIONS.filter(o => (!min || o.value >= min) && (!max || o.value <= max))
}

const previewTitle       = computed(() => props.title.trim() || 'Your question here...')
const previewPlaceholder = computed(() => props.questionDetails.placeholder?.trim() || '')
const previewMaxLength   = computed(() => {
  const ml = props.questionDetails.max_length
  if (ml === '' || ml === null || ml === undefined) return null
  const n = parseInt(ml)
  return n >= 1 ? n : null
})
const previewCharsRemaining = computed(() =>
  previewMaxLength.value !== null ? previewMaxLength.value - (previewAnswer.value?.length || 0) : null
)
const previewOptions = computed(() => (props.questionDetails.options || []).filter(o => o.trim()))

// Reset preview state when the question type changes
watch(() => props.questionType, () => {
  previewAnswer.value = props.questionDetails.default?.trim() || ''
  previewChecked.splice(0)
  previewRating.value = null
  previewDateRange.start = null
  previewDateRange.end   = null
})

// Reflect default value changes in the preview immediately
watch(() => props.questionDetails.default, (val) => {
  if (!['rating', 'file_upload', 'signature'].includes(props.questionType)) {
    previewAnswer.value = val?.trim() || ''
  }
}, { immediate: true })
</script>
